/**
 * The capabilities header is MANDATORY on every `/routes` and `/routes/plan`
 * request. Without `AWAIT_SETTLEMENT` the backend answers `routes: []` for every
 * pair — including same-chain ones — rather than erroring, which is why it is a
 * constant here and not a per-call option. The backend validates it against
 * `^v1;steps=([A-Z_,]+)$` and 400s on anything else.
 *
 * `TRANSACTION` + `AWAIT_SETTLEMENT` is deliberately the whole set: it is what
 * makes the backend return plans the wallet can execute as a plain AccountOp.
 * Adding `SIGN_TYPED_DATA` / `BACKEND_SUBMISSION` would opt into off-chain order
 * flows the wallet has no plan engine for.
 */
export const ZKX_TRADE_CAPABILITIES = 'v1;steps=TRANSACTION,AWAIT_SETTLEMENT'

/** The provider id used on routes and in the controller's provider checks. */
export const ZKX_TRADE_PROVIDER_ID = 'zkxtrade'

/**
 * Chains the backend lists in `/chains` but cannot actually route, so quoting
 * them here would only produce empty-route errors. Empty today.
 *
 * PulseChain (369) lived here until 2026-09-11: the backend listed it but had no
 * route for it, not even same-chain, so it stayed on LibertySwap. That is no
 * longer true — `/tokens?chainId=369` answers with 527 tokens and `/routes`
 * prices 369 → 369 through its `piteas` provider, including PCOCK → USDC, which
 * LibertySwap's own `/app/swap/quote` refuses with `No PulseChain group pool for
 * PCOCK`. `POST /routes/plan` returns an executable approve + swap plan for it.
 * Keep the mechanism for the next chain that needs it.
 */
export const ZKX_TRADE_EXCLUDED_CHAIN_IDS: number[] = []

/**
 * The chains a same-chain pair is routed to ZKX Trade on (see `gate.ts`). This
 * is deliberately a static list rather than the live `/chains` response: the
 * routing decision is taken on the very first quote, before any fetch has
 * landed, and LibertySwap answers the exact negation of it without knowing
 * anything about this provider's fetch state. Mirrors `/chains` as of
 * 2026-09-06, minus the exclusions above.
 */
export const ZKX_TRADE_ROUTED_CHAIN_IDS = [
  1, // Ethereum
  10, // Optimism
  56, // BNB Chain
  137, // Polygon
  143, // Monad
  369, // PulseChain — see ZKX_TRADE_EXCLUDED_CHAIN_IDS for why it moved here
  5000, // Mantle
  8453, // Base
  42161, // Arbitrum
  43114, // Avalanche
  534352 // Scroll
]

/** How long a fetched `/tokens?chainId=` list is reused. */
export const ZKX_TRADE_TOKEN_LIST_TTL = 10 * 60 * 1000 // 10 minutes

/**
 * Per-request cap for the ZKX Trade API. `/tokens` is big (Ethereum is 5.5k
 * tokens / 423KB gzipped) and `/routes` regularly takes 16–19s under load on
 * PulseChain pairs, so leave real headroom rather than failing a quote the
 * router is still computing (Trello #491).
 */
export const ZKX_TRADE_REQUEST_TIMEOUT = 45_000

/** The address the API uses for a chain's native coin. */
export const ZKX_TRADE_NATIVE_TOKEN_ADDRESS = '0x0000000000000000000000000000000000000000'

/**
 * The OTHER spelling of "the chain's own coin": the portfolio and the
 * LibertySwap registry key it by 0xEeee…EEeE, the API by the zero address
 * above. A from-token picked out of the registry therefore arrives spelled this
 * way, and every native check in this service has to accept it — sending it to
 * `/routes` verbatim is answered `400 UNSUPPORTED_TOKEN` (Trello #499: PLS on
 * 369 could not be swapped at all).
 */
export const ZKX_TRADE_NATIVE_ALIAS_ADDRESS = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'

/**
 * Whether to send `Idempotency-Key` on `POST /routes/plan`.
 *
 * MUST stay `true`: without it the backend answers
 * `400 Idempotency-Key header must contain 1..200 characters`.
 *
 * It used to make the endpoint unreachable from a browser: asking for the header
 * in a CORS preflight made the API gateway answer `204` with no
 * `Access-Control-Allow-*` headers at all, so the browser blocked the POST and
 * the wallet saw `Failed to fetch` (only curl, which does not preflight, got
 * through). The backend added `Idempotency-Key` to the gateway's
 * `Access-Control-Allow-Headers` — preflight and POST both verified from the
 * app's own origin on 2026-09-08.
 *
 * The flag stays because the dependency is still real, not because it is a knob
 * to turn: switching it off trades the (now-fixed) CORS failure for a 400.
 */
export const ZKX_TRADE_SEND_IDEMPOTENCY_KEY = true
